import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchHeroSlides, type HeroSlide } from '@/api/client';
import { heroSlides as mockHeroSlides } from '@/mocks/heroSlides';
import ScrollReveal from '@/components/base/ScrollReveal';

export default function HeroSlider({ onOpenQuote }: { onOpenQuote?: () => void }) {
  const { t } = useTranslation('home');
  const [slides, setSlides] = useState<HeroSlide[]>(mockHeroSlides);
  const [current, setCurrent] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchHeroSlides().then((data) => {
      if (cancelled) return;
      if (data.length === 0) {
        setSlides(mockHeroSlides);
        return;
      }
      const merged = data.map((apiSlide, i) => {
        const fallback = mockHeroSlides[i] || mockHeroSlides[0];
        return {
          ...fallback,
          ...apiSlide,
          image: apiSlide.image?.trim() || fallback.image,
        };
      });
      setSlides(merged);
    }).catch(() => {
      if (!cancelled) setSlides(mockHeroSlides);
    });
    return () => { cancelled = true; };
  }, []);

  const total = slides.length || 1;

  const nextSlide = useCallback(() => {
    setCurrent((prev) => (prev + 1) % total);
  }, [total]);

  const prevSlide = useCallback(() => {
    setCurrent((prev) => (prev - 1 + total) % total);
  }, [total]);

  const goToSlide = (index: number) => {
    setCurrent(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  useEffect(() => {
    if (!isAutoPlaying || total <= 1) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide, total]);

  const handleNext = () => {
    setIsAutoPlaying(false);
    nextSlide();
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const handlePrev = () => {
    setIsAutoPlaying(false);
    prevSlide();
    setTimeout(() => setIsAutoPlaying(true), 8000);
  };

  const slide = slides[current];

  // Use slide data directly — API values take priority over i18n translations
  const getSlideText = (index: number, type: 'tag' | 'title' | 'subtitle'): string => {
    const slideData = slides[index];
    if (!slideData) return '';
    if (type === 'tag') return slideData.tag || '';
    if (type === 'title') return slideData.title || '';
    return slideData.subtitle || '';
  };

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-brand-dark">
      {slides.map((s, index) => (
        <div
          key={s.id ?? index}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            index === current ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${s.image})` }}
          />
          {/* Premium blue overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/80 via-teal-800/60 to-sky-900/50" />

          {/* Content */}
          <div className="relative z-20 h-full flex items-center px-4 md:px-16 lg:px-24">
            <ScrollReveal className="max-w-2xl" delay={100}>
              <span className="inline-block px-3 py-1 text-xs font-semibold tracking-wider uppercase text-white bg-brand-mint/90 rounded-md mb-4">
                {getSlideText(index, 'tag')}
              </span>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-heading leading-tight mb-4">
                {getSlideText(index, 'title')}
              </h1>
              <p className="text-base md:text-lg text-white/85 font-body mb-6 max-w-lg leading-relaxed">
                {getSlideText(index, 'subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={onOpenQuote}
                  className="inline-flex items-center justify-center px-6 py-3 bg-brand-mint text-white font-semibold rounded-lg hover:bg-brand-mint/90 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('quote_cta')}
                </button>
                <a
                  href="#services"
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap"
                >
                  {t('hero_cta_secondary')}
                </a>
              </div>
            </ScrollReveal>
          </div>
        </div>
      ))}

      {/* Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition-colors cursor-pointer"
            aria-label={t('admin_prev_slide')}
          >
            <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/40 transition-colors cursor-pointer"
            aria-label={t('admin_next_slide')}
          >
            <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
          </button>
        </>
      )}

      {/* Dots */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full cursor-pointer ${
                index === current
                  ? 'w-8 h-3 bg-brand-mint'
                  : 'w-3 h-3 bg-white/60 hover:bg-white'
              }`}
              aria-label={`${t('admin_go_slide')} ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}